const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function getToken(path?: string): string | null {
  // Portal endpoints use client token, everything else uses admin token
  const key = path?.startsWith('/portal') || path?.startsWith('/client-auth')
    ? 'vop_client_user'
    : 'vop_user';
  const user = localStorage.getItem(key);
  if (!user) return null;
  try {
    return JSON.parse(user).token || null;
  } catch {
    return null;
  }
}

async function request<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken(path);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }

  return res.json();
}

// ─── Auth ─────────────────────────────────────────────────
export const api = {
  auth: {
    login: (email: string, password: string) =>
      request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    register: (data: { email: string; password: string; name: string; role?: string }) =>
      request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  },

  // ─── Clients ──────────────────────────────────────────────
  clients: {
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/clients${qs}`);
    },
    get: (id: string) => request(`/clients/${id}`),
    create: (data: any) => request('/clients', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) => request(`/clients/${id}`, { method: 'DELETE' }),
    stats: () => request('/clients/stats/summary'),
    addBeneficiary: (clientId: string, data: any) =>
      request(`/clients/${clientId}/beneficiaries`, { method: 'POST', body: JSON.stringify(data) }),
    deleteBeneficiary: (beneficiaryId: string) =>
      request(`/clients/beneficiaries/${beneficiaryId}`, { method: 'DELETE' }),
    addContactLog: (clientId: string, data: any) =>
      request(`/clients/${clientId}/contact-logs`, { method: 'POST', body: JSON.stringify(data) }),
  },

  // ─── Sales ────────────────────────────────────────────────
  sales: {
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/sales${qs}`);
    },
    get: (id: string) => request(`/sales/${id}`),
    create: (data: any) => request('/sales', { method: 'POST', body: JSON.stringify(data) }),
    updateStage: (id: string, stage: string) =>
      request(`/sales/${id}/stage`, { method: 'PUT', body: JSON.stringify({ stage }) }),
    update: (id: string, data: any) => request(`/sales/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    recordPayment: (paymentId: string, data: any) =>
      request(`/sales/payments/${paymentId}/pay`, { method: 'POST', body: JSON.stringify(data) }),
    stats: () => request('/sales/stats/summary'),
  },

  // ─── Lots (legacy) ──────────────────────────────────────────
  lots: {
    list: () => request('/lots'),
    create: (data: any) => request('/lots', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/lots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  },

  // ─── Inventory (full CRUD) ─────────────────────────────────
  inventory: {
    // Sections
    sections: {
      list: () => request('/inventory/sections'),
      get: (id: string) => request(`/inventory/sections/${id}`),
      create: (data: any) => request('/inventory/sections', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/inventory/sections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/inventory/sections/${id}`, { method: 'DELETE' }),
    },
    // Lot Types
    lotTypes: {
      list: () => request('/inventory/lot-types'),
      create: (data: any) => request('/inventory/lot-types', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/inventory/lot-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/inventory/lot-types/${id}`, { method: 'DELETE' }),
    },
    // Lots
    lots: {
      list: (params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request(`/inventory/lots${qs}`);
      },
      get: (id: string) => request(`/inventory/lots/${id}`),
      create: (data: any) => request('/inventory/lots', { method: 'POST', body: JSON.stringify(data) }),
      batchCreate: (data: any) => request('/inventory/lots/batch', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/inventory/lots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/inventory/lots/${id}`, { method: 'DELETE' }),
    },
    // Transactions (Map → Sale)
    transactions: {
      context: (lotId: string) => request(`/inventory/lots/${lotId}/transaction`),
      reserve: (lotId: string, data: { clientId: string; agentId: string; saleAmount: number; paymentType?: string; downPayment?: number; installmentMonths?: number; notes?: string; performedBy?: string }) =>
        request(`/inventory/lots/${lotId}/reserve`, { method: 'POST', body: JSON.stringify(data) }),
      release: (lotId: string, data?: { reason?: string; performedBy?: string }) =>
        request(`/inventory/lots/${lotId}/release`, { method: 'POST', body: JSON.stringify(data || {}) }),
      completeSale: (lotId: string, data?: { performedBy?: string }) =>
        request(`/inventory/lots/${lotId}/complete-sale`, { method: 'POST', body: JSON.stringify(data || {}) }),
      searchClients: (q: string) => request(`/inventory/clients/search?q=${encodeURIComponent(q)}`),
      agents: () => request('/inventory/agents'),
    },
    // Audit Logs
    auditLogs: {
      list: (lotId: string) => request(`/inventory/lots/${lotId}/audit-logs`),
      create: (lotId: string, data: any) => request(`/inventory/lots/${lotId}/audit-logs`, { method: 'POST', body: JSON.stringify(data) }),
    },
    // Policies
    policies: {
      list: () => request('/inventory/policies'),
      update: (id: string, data: any) => request(`/inventory/policies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    },
  },

  // ─── Dashboard ─────────────────────────────────────────────
  dashboard: {
    stats: () => request('/dashboard/stats'),
  },

  // ─── Reports ──────────────────────────────────────────────
  reports: {
    summary: () => request('/reports/summary'),
    aging: () => request('/reports/aging'),
    revenueTrend: () => request('/reports/revenue-trend'),
  },

  // ─── Commission Rules ─────────────────────────────────────
  commissionRules: {
    list: () => request('/commission-rules'),
    get: (id: string) => request(`/commission-rules/${id}`),
    create: (data: any) => request('/commission-rules', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/commission-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) => request(`/commission-rules/${id}`, { method: 'DELETE' }),
    toggle: (id: string) => request(`/commission-rules/${id}/toggle`, { method: 'PATCH' }),
    test: (data: { saleAmount: number; paymentType?: string; agentRole?: string; sectionId?: string; agentId?: string; ruleIds?: string[] }) =>
      request('/commission-rules/test', { method: 'POST', body: JSON.stringify(data) }),
    agentSummary: () => request('/commission-rules/reports/agent-summary'),
  },

  // ─── Notifications ──────────────────────────────────────────
  notifications: {
    list: (userId: string, params?: Record<string, string>) => {
      const qs = params ? '&' + new URLSearchParams(params).toString() : '';
      return request(`/notifications/${userId}?limit=30${qs}`);
    },
    markRead: (id: string) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
    markAllRead: (userId: string) => request(`/notifications/user/${userId}/read-all`, { method: 'PATCH' }),
    delete: (id: string) => request(`/notifications/${id}`, { method: 'DELETE' }),
    generate: (userId: string) => request(`/notifications/generate/${userId}`, { method: 'POST' }),
  },

  // ─── Documents (PDF) ──────────────────────────────────────
  documents: {
    list: (saleId: string) => request(`/documents/list/${saleId}`),
    saleContractUrl: (saleId: string) => `${API_BASE}/documents/sale-contract/${saleId}`,
    soaUrl: (saleId: string) => `${API_BASE}/documents/soa/${saleId}`,
    paymentReceiptUrl: (paymentId: string) => `${API_BASE}/documents/payment-receipt/${paymentId}`,
  },

  // ─── Payments + Ledger ──────────────────────────────────────
  payments: {
    // Schedules
    schedules: {
      list: (params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request(`/payments/schedules${qs}`);
      },
      get: (id: string) => request(`/payments/schedules/${id}`),
      generate: (data: { saleId: string; frequency?: string; startDate?: string; customDates?: string[] }) =>
        request('/payments/schedules/generate', { method: 'POST', body: JSON.stringify(data) }),
    },
    // Record payment (FIFO)
    record: (data: { saleId: string; amount: number; paymentMethod?: string; referenceNumber?: string; receiptNumber?: string; notes?: string; performedBy?: string }) =>
      request('/payments/record', { method: 'POST', body: JSON.stringify(data) }),
    // Overdue
    overdue: {
      detect: () => request('/payments/overdue/detect', { method: 'POST' }),
      summary: () => request('/payments/overdue/summary'),
    },
    // Penalties
    penalties: {
      compute: (data?: { penaltyRate?: number; minDaysOverdue?: number }) =>
        request('/payments/penalties/compute', { method: 'POST', body: JSON.stringify(data || {}) }),
    },
    // Ledger
    ledger: {
      list: (params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request(`/payments/ledger${qs}`);
      },
      create: (data: { clientId: string; saleId?: string; type: string; amount: number; description: string; referenceNumber?: string; performedBy?: string }) =>
        request('/payments/ledger', { method: 'POST', body: JSON.stringify(data) }),
    },
    // Balance
    balance: (clientId: string) => request(`/payments/balance/${clientId}`),
    // Stats
    stats: () => request('/payments/stats'),
  },

  // ─── Users & Roles ──────────────────────────────────────
  users: {
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/users${qs}`);
    },
    get: (id: string) => request(`/users/${id}`),
    create: (data: any) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    toggleStatus: (id: string) => request(`/users/${id}/toggle-status`, { method: 'PATCH' }),
    delete: (id: string) => request(`/users/${id}`, { method: 'DELETE' }),
    myPermissions: () => request('/users/me/permissions'),
    // Roles
    roles: {
      list: () => request('/users/roles/list'),
      get: (id: string) => request(`/users/roles/${id}`),
      create: (data: any) => request('/users/roles', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/users/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/users/roles/${id}`, { method: 'DELETE' }),
      updatePermissions: (id: string, permissions: { module: string; action: string }[]) =>
        request(`/users/roles/${id}/permissions`, { method: 'PUT', body: JSON.stringify({ permissions }) }),
    },
  },

  // ─── Client Auth ─────────────────────────────────────────
  clientAuth: {
    invite: (clientId: string) =>
      request('/client-auth/invite', { method: 'POST', body: JSON.stringify({ clientId }) }),
    verifyInvite: (token: string) => request(`/client-auth/verify-invite/${token}`),
    setupPassword: (token: string, password: string) =>
      request('/client-auth/setup-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
    login: (email: string, password: string) =>
      request('/client-auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    forgotPassword: (email: string) =>
      request('/client-auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
    resetPassword: (token: string, password: string) =>
      request('/client-auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
    status: (clientId: string) => request(`/client-auth/status/${clientId}`),
  },

  // ─── Client Portal ─────────────────────────────────────────
  portal: {
    dashboard: () => request('/portal/dashboard'),
    me: () => request('/portal/me'),
    updateProfile: (data: any) => request('/portal/me', { method: 'PUT', body: JSON.stringify(data) }),
    changePassword: (currentPassword: string, newPassword: string) =>
      request('/portal/me/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),
    purchases: () => request('/portal/purchases'),
    purchase: (id: string) => request(`/portal/purchases/${id}`),
    payments: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/portal/payments${qs}`);
    },
    documents: () => request('/portal/documents'),
  },

  // ─── Global Search ───────────────────────────────────────
  search: (q: string) => request(`/search?q=${encodeURIComponent(q)}`),

  // ─── Refunds ──────────────────────────────────────────────
  refunds: {
    // Policies
    policies: {
      list: () => request('/refunds/policies'),
      get: (id: string) => request(`/refunds/policies/${id}`),
      create: (data: any) => request('/refunds/policies', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/refunds/policies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/refunds/policies/${id}`, { method: 'DELETE' }),
      toggle: (id: string) => request(`/refunds/policies/${id}/toggle`, { method: 'PATCH' }),
    },
    // Calculate refund
    calculate: (data: { saleId: string; policyId?: string }) =>
      request('/refunds/calculate', { method: 'POST', body: JSON.stringify(data) }),
    // Process refund
    process: (data: { saleId: string; policyId?: string; refundAmount: number; refundMethod: string; referenceNumber?: string; notes?: string; performedBy?: string }) =>
      request('/refunds/process', { method: 'POST', body: JSON.stringify(data) }),
  },
};
